// AI Voortgang — per fase akkoord van de klant vragen bij AI-videoprojecten (plan:
// docs/ai-voortgang-v1.md). Werkpagina: links de projecten (tool-eigen kolom), rechts de
// fases met per deliverable de versies, de akkoordstatus, wijzigingsverzoeken en de
// revisieteller. De klant beslist in het klantportaal (flow → Akkoord).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Link, Menu, MenuItem, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { voerUitgesteldUit } from '../../utils/uitgesteld';
import { meldFout } from '../../utils/apiFout';
import { useUserRole } from '../../contexts/UserRoleContext';
import NieuwProjectDialoog, { AccountKeuze, bevestigKoppeling } from './NieuwProjectDialoog';
import { Inbox, Opmerkingen, Verzoeken } from './Klantcontact';
import {
  api, haalPortaalAccounts, haalProject, lijstProjecten, nieuwProject, nieuweVersie, trekTerug, vraagAkkoord,
  wijzigProject, zetOvergeslagen, uploadBestand, UPLOAD_TYPES, nieuweOptie, hernoemOptie, zetJuridisch, haalBeoordelaars, zetRol, ROLLEN, veiligeLink, datumTijd,
  heropenFase, downloadBewijs, ACCENT, FASE_STATUS, INKT, ITEM_STATUS, KOLOM_BG, LIJN, STIL, WAARSCHUWING,
} from './voortgangApi';

function Chip({ s }) {
  return (
    <Box component="span" sx={{ px: 1, py: 0.25, borderRadius: 10, fontSize: 12, fontWeight: 600, color: s.kleur, bgcolor: s.bg, whiteSpace: 'nowrap' }}>
      {s.label}
    </Box>
  );
}

function ProjectRij({ p, actief, onClick }) {
  const signaal = p.wijziging_gevraagd ? `${p.wijziging_gevraagd} wijziging gevraagd`
    : p.wacht_op_klant ? `wacht op klant (${p.wacht_op_klant})` : p.huidige_fase?.naam || 'afgerond';
  return (
    <ButtonBase onClick={onClick} sx={{ display: 'block', width: '100%', textAlign: 'left', px: 1.25, py: 1, borderRadius: 1.5,
      bgcolor: actief ? '#eceff3' : 'transparent', '&:hover': { bgcolor: actief ? '#eceff3' : '#f3f4f6' } }}>
      <Typography noWrap sx={{ fontSize: 13.5, fontWeight: actief ? 700 : 500, color: INKT }}>{p.titel}</Typography>
      <Typography noWrap sx={{ fontSize: 12, color: p.wijziging_gevraagd ? '#b91c1c' : STIL }}>
        {[p.project_number, signaal].filter(Boolean).join(' · ')}
      </Typography>
    </ButtonBase>
  );
}

function rondeTekst(item) {
  return `Ronde ${item.rondes_gebruikt + 1} · ${item.rondes_inbegrepen} correctieronde${item.rondes_inbegrepen === 1 ? '' : 's'} inbegrepen`;
}

function BestandUploaden({ onUpload }) {
  const [voortgang, setVoortgang] = useState(null);
  const invoer = useRef(null);
  async function kies(e) {
    const bestand = e.target.files?.[0];
    e.target.value = '';
    if (!bestand) return;
    if (bestand.size > 5 * 1024 ** 3) { meldFout(new Error('Bestand is groter dan 5 GB'), 'Uploaden'); return; }
    setVoortgang(0);
    try { await onUpload(bestand, setVoortgang); } finally { setVoortgang(null); }
  }
  return (
    <>
      <Button size="small" variant="outlined" onClick={() => invoer.current?.click()} disabled={voortgang !== null}
        sx={{ textTransform: 'none' }}>
        {voortgang === null ? 'Bestand uploaden' : `Uploaden… ${Math.round(voortgang * 100)}%`}
      </Button>
      <input ref={invoer} type="file" hidden accept={UPLOAD_TYPES} onChange={kies} />
    </>
  );
}

function VersieToevoegen({ onToevoegen, onUpload }) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [bezig, setBezig] = useState(false);
  const voegToe = async () => {
    if (!url.trim() || bezig) return;
    setBezig(true);
    const ok = await onToevoegen(url.trim(), label.trim());
    setBezig(false);
    if (ok) { setUrl(''); setLabel(''); }
  };
  return (
    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
      <TextField size="small" placeholder="Frame.io- of bestandslink (https://…)" value={url} onChange={(e) => setUrl(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }} sx={{ flex: '2 1 260px' }} inputProps={{ 'aria-label': 'Link van de versie' }} />
      <TextField size="small" placeholder="Label (optioneel)" value={label} onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') voegToe(); }} sx={{ flex: '1 1 140px' }} inputProps={{ 'aria-label': 'Label van de versie', maxLength: 200 }} />
      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={voegToe} disabled={!url.trim() || bezig}
        sx={{ textTransform: 'none' }}>Versie toevoegen</Button>
      {onUpload && <BestandUploaden onUpload={(bestand, setVoortgang) => onUpload(bestand, label.trim(), setVoortgang)} />}
    </Box>
  );
}

function VersieRij({ v, canEdit, onVerwijderVersie }) {
  const link = veiligeLink(v.link);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: INKT, minWidth: 28 }}>v{v.versie}</Typography>
      <Typography noWrap sx={{ fontSize: 13, color: INKT, flex: 1, minWidth: 0 }}>
        {link ? (
          <Link href={link} target="_blank" rel="noopener noreferrer">
            {v.media?.soort && v.media.soort !== 'link' ? `${{ video: '🎬', image: '🖼️', pdf: '📄' }[v.media.soort] || ''} ` : ''}
            {v.label || v.media?.naam || (v.bron === 'frameio' ? 'Frame.io' : 'Link')}
          </Link>
        ) : <span style={{ color: STIL }}>(nog geen bestand of link)</span>}
        {v.media?.bytes ? <span style={{ color: STIL }}> · {(v.media.bytes / 1024 / 1024).toFixed(1)} MB</span> : null}
        <span style={{ color: STIL }}>{v.datum ? ` · ${datumTijd(v.datum)}` : ''}{v.door ? ` · ${v.door.split('@')[0]}` : ''}</span>
      </Typography>
      {v.status === 'published' && (
        <Tooltip title={(v.gezien || []).map((g) => `${g.door} · ${datumTijd(g.op)}`).join('\n') || 'Nog door niemand bij de klant geopend'}>
          <Typography sx={{ fontSize: 12, color: v.gezien?.length ? INKT : STIL, whiteSpace: 'nowrap' }}>
            {v.gezien?.length ? `👁 ${v.gezien.length}× gezien` : 'nog niet gezien'}
          </Typography>
        </Tooltip>
      )}
      <Typography sx={{ fontSize: 12, color: v.status === 'published' ? '#15803d' : STIL }}>
        {v.status === 'published' ? 'gedeeld' : 'concept'}
      </Typography>
      {canEdit && v.status === 'draft' && link && (
        <Tooltip title="Concept verwijderen">
          <IconButton size="small" aria-label={`Versie ${v.versie} verwijderen`} onClick={() => onVerwijderVersie(v)} sx={{ color: STIL }}>
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function OptieBlok({ optie, keuze, gekozen, canEdit, kanVersie, kanBeheren, onVersie, onUpload, onVerwijderVersie, onHernoem, onVerwijderOptie }) {
  const versies = optie.versies || [];
  const ooitGedeeld = versies.some((v) => v.status === 'published');
  return (
    <Box sx={keuze ? { mt: 1, p: 1.25, borderRadius: 1.5, border: `1px solid ${gekozen ? '#15803d' : LIJN}`, bgcolor: gekozen ? '#f0fdf4' : '#fff' } : {}}>
      {keuze && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INKT, flex: 1 }}>
            {optie.label}{gekozen ? <span style={{ color: '#15803d' }}> · ✓ gekozen door de klant</span> : ''}
          </Typography>
          {kanBeheren && <Button size="small" onClick={() => onHernoem(optie)} sx={{ textTransform: 'none', color: STIL, minWidth: 0 }}>Hernoemen</Button>}
          {kanBeheren && !ooitGedeeld && (
            <Tooltip title="Optie verwijderen">
              <IconButton size="small" aria-label={`${optie.label} verwijderen`} onClick={() => onVerwijderOptie(optie)} sx={{ color: STIL }}>
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      {versies.length === 0 && <Typography sx={{ fontSize: 13, color: STIL, mt: 0.5 }}>Nog geen versie.</Typography>}
      {versies.map((v) => <VersieRij key={v.id} v={v} canEdit={canEdit} onVerwijderVersie={onVerwijderVersie} />)}
      {kanVersie && <VersieToevoegen onToevoegen={(url, label) => onVersie(optie.id, url, label)}
        onUpload={onUpload ? (bestand, label, setVoortgang) => onUpload(optie.id, bestand, label, setVoortgang) : undefined} />}
    </Box>
  );
}

function Tekenen({ item }) {
  if (!item.tekenen || item.tekenen.length < 2) return null;
  return (
    <Typography sx={{ mt: 0.5, fontSize: 12, color: STIL }}>
      Tekenen: {item.tekenen.map((t) => `${t.naam} ${t.door ? `✓ ${t.door}` : '— nog open'}`).join(' · ')}
    </Typography>
  );
}

function ItemBlok({ item, fase, canEdit, onVersie, onUpload, onVerwijderVersie, onNieuweOptie, onHernoem, onVerwijderOptie, onJuridisch, onProject }) {
  const open = (item.wijzigingsverzoeken || []).filter((w) => w.status === 'requested');
  const kanVersie = canEdit && item.status !== 'approved' && !['approved', 'skipped'].includes(fase.status);
  const kanBeheren = kanVersie && fase.status !== 'awaiting_approval';
  const buitenRondes = item.rondes_gebruikt > item.rondes_inbegrepen
    || (item.status === 'changes_requested' && item.volgende_ronde_extra);
  return (
    <Box id={`item-${item.id}`} sx={{ py: 1.5, borderTop: `1px solid ${LIJN}`, '&:first-of-type': { borderTop: 0 }, scrollMarginTop: 80 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: INKT, flex: 1, minWidth: 0 }}>
          {item.label}{item.keuze ? <span style={{ color: STIL, fontWeight: 500 }}> · klant kiest 1 van {item.opties.length}</span> : ''}
        </Typography>
        <Typography sx={{ fontSize: 12, color: buitenRondes ? WAARSCHUWING : STIL, fontWeight: buitenRondes ? 700 : 400 }}>
          {buitenRondes ? '⚠ ' : ''}{rondeTekst(item)}
        </Typography>
        <Chip s={ITEM_STATUS[item.status] || ITEM_STATUS.pending} />
      </Box>

      <Tekenen item={item} />
      {item.opties.map((o) => (
        <OptieBlok key={o.id} optie={o} keuze={item.keuze} gekozen={item.gekozen_optie_id === o.id} canEdit={canEdit}
          kanVersie={kanVersie} kanBeheren={kanBeheren && item.opties.length > 1} onVersie={onVersie} onUpload={onUpload}
          onVerwijderVersie={onVerwijderVersie} onHernoem={onHernoem} onVerwijderOptie={onVerwijderOptie} />
      ))}
      {kanBeheren && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<AddIcon />} onClick={() => onNieuweOptie(item)} sx={{ textTransform: 'none', color: STIL }}>
            {item.keuze ? 'Nog een optie' : 'Opties laten kiezen (A/B)'}
          </Button>
          <Button size="small" onClick={() => onJuridisch(item)} sx={{ textTransform: 'none', color: STIL }}>
            {(item.tekenen || []).some((t) => t.rol === 'legal') ? '✓ Ook juridisch akkoord (uitzetten)' : 'Ook juridisch akkoord vragen'}
          </Button>
        </Box>
      )}

      {open.map((w) => (
        <Box key={w.id} sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: '#fef2f2', border: '1px solid #fee2e2' }}>
          <Typography sx={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>
            Wijziging gevraagd{w.door ? ` door ${w.door}` : ''} · {datumTijd(w.op)}{w.soort === 'extra_revision' ? ' · extra ronde (kosten gezien)' : ''}
          </Typography>
          <Typography sx={{ fontSize: 13, color: INKT, whiteSpace: 'pre-wrap' }}>{w.toelichting}</Typography>
        </Box>
      ))}

      {item.besluiten.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          {item.besluiten.map((b) => (
            <Typography key={b.id} sx={{ fontSize: 12, color: STIL }}>
              {b.besluit === 'akkoord' ? '✓ Akkoord' : '↺ Wijziging'}
              {b.optie_id && item.keuze ? ` op ${(item.opties.find((o) => o.id === b.optie_id) || {}).label || 'optie'}` : ''}
              {' '}· {b.door || 'klant'} · {datumTijd(b.op)}
              {b.toelichting && b.besluit === 'akkoord' ? ` · “${b.toelichting}”` : ''}
              {b.besluit === 'akkoord' && (
                <> · <Link component="button" sx={{ fontSize: 12, verticalAlign: 'baseline' }}
                  onClick={() => downloadBewijs(b.id).catch((e) => meldFout(e, 'Akkoordbewijs'))}>PDF-bewijs</Link></>
              )}
            </Typography>
          ))}
        </Box>
      )}
      <Opmerkingen item={item} versies={item.opties.flatMap((o) => o.versies || [])} canEdit={canEdit} onProject={onProject} />
    </Box>
  );
}

function FaseMenu({ fase, onOverslaan, onHeropen }) {
  const [anker, setAnker] = useState(null);
  const kanOverslaan = !['approved', 'awaiting_approval', 'skipped'].includes(fase.status);
  if (!kanOverslaan && !['skipped', 'approved'].includes(fase.status)) return null;
  return (
    <>
      <IconButton size="small" aria-label={`Meer voor fase ${fase.naam}`} onClick={(e) => setAnker(e.currentTarget)} sx={{ color: STIL }}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anker} open={!!anker} onClose={() => setAnker(null)}>
        {kanOverslaan && <MenuItem sx={{ fontSize: 13.5 }} onClick={() => { setAnker(null); onOverslaan(fase, true); }}>Fase overslaan</MenuItem>}
        {fase.status === 'skipped' && <MenuItem sx={{ fontSize: 13.5 }} onClick={() => { setAnker(null); onOverslaan(fase, false); }}>Terugzetten</MenuItem>}
        {fase.status === 'approved' && <MenuItem sx={{ fontSize: 13.5 }} onClick={() => { setAnker(null); onHeropen(fase); }}>Fase heropenen…</MenuItem>}
      </Menu>
    </>
  );
}

function FaseKaart({ fase, bezig, canEdit, onVraagAkkoord, onTrekTerug, onVersie, onVerwijderVersie, onOverslaan, onHeropen, optieActies }) {
  const status = FASE_STATUS[fase.status] || FASE_STATUS.locked;
  const kanVragen = canEdit && ['in_production', 'awaiting_approval', 'reopened'].includes(fase.status);
  const gedimd = ['locked', 'skipped'].includes(fase.status);
  return (
    <Box sx={{ mb: 2, border: `1px solid ${LIJN}`, borderRadius: 2, bgcolor: '#fff', opacity: gedimd ? 0.8 : 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, borderBottom: `1px solid ${LIJN}`, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: STIL, minWidth: 18 }}>{fase.positie}</Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: INKT, flex: 1, minWidth: 0 }}>{fase.naam}</Typography>
        {fase.status === 'awaiting_approval' && fase.gevraagd_op && (
          <Typography sx={{ fontSize: 12, color: STIL }}>gevraagd {datumTijd(fase.gevraagd_op)}</Typography>
        )}
        {fase.status === 'approved' && fase.akkoord_op && (
          <Typography sx={{ fontSize: 12, color: STIL }}>akkoord {datumTijd(fase.akkoord_op)}</Typography>
        )}
        <Chip s={status} />
        {canEdit && fase.status === 'awaiting_approval' && (
          <Button size="small" onClick={() => onTrekTerug(fase)} disabled={bezig} sx={{ textTransform: 'none', color: STIL }}>
            Terugtrekken
          </Button>
        )}
        {kanVragen && (
          <Button size="small" variant="contained" disableElevation onClick={() => onVraagAkkoord(fase)} disabled={bezig}
            sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
            {fase.status === 'awaiting_approval' ? 'Opnieuw akkoord vragen' : 'Akkoord vragen'}
          </Button>
        )}
        {canEdit && <FaseMenu fase={fase} onOverslaan={onOverslaan} onHeropen={onHeropen} />}
      </Box>
      {!gedimd && (
        <Box sx={{ px: 2, py: 0.5 }}>
          {fase.items.map((i) => (
            <ItemBlok key={i.id} item={i} fase={fase} canEdit={canEdit} onVersie={onVersie} onVerwijderVersie={onVerwijderVersie} {...optieActies} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function AiVoortgang() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { permissions } = useUserRole();
  // Server dwingt het recht af; hier verbergen we bediening die toch 403 zou geven.
  const canEdit = !!permissions?.ai_voortgang?.edit;
  const [projecten, setProjecten] = useState(null);
  const [detail, setDetail] = useState(null);
  const [laden, setLaden] = useState(false);
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [meerAnker, setMeerAnker] = useState(null);
  const [wijzigOpen, setWijzigOpen] = useState(false);
  const [wijzigForm, setWijzigForm] = useState({ titel: '', revisierondes: 2, portal_account_id: '' });
  const [accounts, setAccounts] = useState(null);
  const [accountFout, setAccountFout] = useState('');
  const [bezig, setBezig] = useState(false);
  const huidigId = useRef(projectId);
  huidigId.current = projectId;

  useEffect(() => registreerOngedaanSneltoets(), []);

  const laadLijst = useCallback(async () => {
    try { setProjecten((await lijstProjecten()).projecten || []); }
    catch (e) { meldFout(e, 'Projecten laden'); setProjecten([]); }
  }, []);
  useEffect(() => { laadLijst(); }, [laadLijst]);

  useEffect(() => {
    if (!projectId) { setDetail(null); return undefined; }
    let weg = false;
    setLaden(true);
    haalProject(projectId)
      .then((d) => { if (!weg) setDetail(d); })
      .catch((e) => { if (!weg) { meldFout(e, 'Project laden'); setDetail(null); } })
      .finally(() => { if (!weg) setLaden(false); });
    return () => { weg = true; };
  }, [projectId]);

  // Antwoord alleen toepassen als we nog op hetzelfde project staan; de lijst volgt mee.
  const toon = useCallback((p) => {
    if (!p) return;
    if (p.id === huidigId.current) setDetail(p);
    setProjecten((lijst) => (lijst || []).map((x) => (x.id === p.id ? { ...x, ...p, fases: undefined } : x)));
  }, []);

  async function metSlot(taak) {
    if (bezig) return undefined;
    setBezig(true);
    try { return await taak(); } finally { setBezig(false); }
  }

  async function versieToevoegen(optieId, url, label) {
    try {
      const uit = await nieuweVersie(optieId, { url, label });
      toon(uit.project);
      toast.success(`Versie ${uit.versie} toegevoegd als concept — de klant ziet hem na "Akkoord vragen"`);
      return true;
    } catch (e) { meldFout(e, 'Versie toevoegen'); return false; }
  }

  async function versieVerwijderen(v) {
    const ok = await bevestig({
      titel: `Versie ${v.versie} verwijderen?`,
      tekst: 'Dit concept is nog niet met de klant gedeeld en wordt definitief verwijderd.',
      knop: 'Verwijderen', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      const uit = await api(`/versies/${v.id}`, { method: 'DELETE', wat: 'Versie verwijderen' });
      toon(uit.project);
    } catch (e) { meldFout(e, 'Versie verwijderen'); }
  }

  async function akkoordVragen(fase) {
    await metSlot(async () => {
      const r = await voerUitgesteldUit(`Akkoord vragen voor "${fase.naam}" — de klant ziet het in het portaal…`,
        () => vraagAkkoord(fase.id), { duur: 5000, klaarTekst: `Akkoord gevraagd voor "${fase.naam}"` });
      if (r.geannuleerd || r.fout) return;
      toon(r.resultaat.project);
      if (r.resultaat.over_budget?.length) {
        toast(`Let op: ${r.resultaat.over_budget.join(', ')} gaat over de inbegrepen correctierondes`, { icon: '⚠️', duration: 8000 });
      }
    });
  }

  async function terugtrekken(fase) {
    const ok = await bevestig({
      titel: 'Akkoordverzoek terugtrekken?',
      tekst: `De klant kan voor "${fase.naam}" dan niet meer beslissen tot je opnieuw akkoord vraagt. Wat al gedeeld is, blijft zichtbaar.`,
      knop: 'Terugtrekken',
    });
    if (!ok) return;
    await metSlot(async () => {
      try { toon((await trekTerug(fase.id)).project); }
      catch (e) { meldFout(e, 'Terugtrekken'); }
    });
  }

  const [hernoem, setHernoem] = useState(null); // { optie, label }

  async function optieErbij(item) {
    await metSlot(async () => {
      try { toon((await nieuweOptie(item.id, '')).project); }
      catch (e) { meldFout(e, 'Optie toevoegen'); }
    });
  }

  async function bewaarHernoem() {
    if (!hernoem?.label.trim()) return;
    await metSlot(async () => {
      try { toon((await hernoemOptie(hernoem.optie.id, hernoem.label.trim())).project); setHernoem(null); }
      catch (e) { meldFout(e, 'Optie hernoemen'); }
    });
  }

  async function optieWeg(optie) {
    const ok = await bevestig({ titel: `${optie.label} verwijderen?`, tekst: 'Deze optie en haar concepten worden verwijderd. Ze is nog niet met de klant gedeeld.', knop: 'Verwijderen', gevaarlijk: true });
    if (!ok) return;
    try { toon((await api(`/opties/${optie.id}`, { method: 'DELETE', wat: 'Optie verwijderen' })).project); }
    catch (e) { meldFout(e, 'Optie verwijderen'); }
  }

  async function juridisch(item) {
    const aan = !(item.tekenen || []).some((t) => t.rol === 'legal');
    await metSlot(async () => {
      try {
        toon((await zetJuridisch(item.id, aan)).project);
        biedOngedaanAan(aan ? `"${item.label}" vraagt nu ook juridisch akkoord` : `Juridisch akkoord voor "${item.label}" uitgezet`,
          async () => toon((await zetJuridisch(item.id, !aan)).project));
      } catch (e) { meldFout(e, 'Opslaan'); }
    });
  }

  const [beoordelaars, setBeoordelaars] = useState(null); // null = dicht, [] = laden/leeg

  async function openBeoordelaars() {
    setMeerAnker(null);
    setBeoordelaars({ laden: true, lijst: [] });
    try { setBeoordelaars({ laden: false, lijst: (await haalBeoordelaars(detail.id)).beoordelaars || [] }); }
    catch (e) { meldFout(e, 'Beoordelaars laden'); setBeoordelaars(null); }
  }

  async function rolWijzigen(b, rol) {
    const vorige = b.rol;
    try {
      setBeoordelaars({ laden: false, lijst: (await zetRol(detail.id, b.id, rol)).beoordelaars || [] });
      biedOngedaanAan(`${b.naam}: ${ROLLEN[rol].label}`, async () => {
        setBeoordelaars({ laden: false, lijst: (await zetRol(detail.id, b.id, vorige)).beoordelaars || [] });
      });
    } catch (e) { meldFout(e, 'Rol opslaan'); }
  }

  async function upload(optieId, bestand, label, setVoortgang) {
    try {
      const uit = await uploadBestand(optieId, bestand, label, setVoortgang);
      toon(uit.project);
      toast.success(`${bestand.name} geüpload als concept — de klant ziet het na "Akkoord vragen"`);
    } catch (e) { meldFout(e, 'Uploaden'); }
  }

  const optieActies = {
    onUpload: upload,
    onNieuweOptie: optieErbij, onHernoem: (o) => setHernoem({ optie: o, label: o.label }), onVerwijderOptie: optieWeg, onJuridisch: juridisch,
    onProject: toon,
  };

  async function heropen(fase) {
    const ok = await bevestig({
      titel: `"${fase.naam}" heropenen?`,
      tekst: 'De onderdelen van deze fase vragen weer een nieuwe versie en akkoord. Latere fases gaan terug naar "volgt" '
        + 'en moeten opnieuw akkoord krijgen. Eerdere besluiten blijven in de audit staan. Vraagt de klant dit zelf? '
        + 'Gebruik dan liever het verzoek van de klant (met offerte).',
      knop: 'Heropenen', gevaarlijk: true,
    });
    if (!ok) return;
    await metSlot(async () => {
      try {
        const uit = await heropenFase(fase.id);
        toon(uit.project);
        toast.success(uit.geraakt?.length ? `Heropend — ook terug naar "volgt": ${uit.geraakt.join(', ')}` : 'Fase heropend');
      } catch (e) { meldFout(e, 'Fase heropenen'); }
    });
  }

  // Vanuit de inbox: naar het onderdeel scrollen zodra het project er staat.
  useEffect(() => {
    const doel = window.location.hash.startsWith('#item-') ? window.location.hash.slice(1) : null;
    if (!detail || !doel) return;
    const el = document.getElementById(doel);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [detail]);

  async function overslaan(fase, aan) {
    await metSlot(async () => {
      try {
        toon((await zetOvergeslagen(fase.id, aan)).project);
        if (aan) {
          biedOngedaanAan(`"${fase.naam}" overgeslagen`, async () => toon((await zetOvergeslagen(fase.id, false)).project));
        }
      } catch (e) { meldFout(e, aan ? 'Fase overslaan' : 'Fase terugzetten'); }
    });
  }

  function laadAccounts() {
    setAccounts(null); setAccountFout('');
    haalPortaalAccounts(detail?.project_airtable_id).then((d) => setAccounts(d.accounts || []))
      .catch((e) => { setAccounts([]); setAccountFout(e?.message || 'Klantportaal niet bereikbaar.'); });
  }

  function openWijzig() {
    setMeerAnker(null);
    setWijzigForm({ titel: detail.titel, revisierondes: detail.revisierondes, portal_account_id: detail.portal_account_id || '' });
    if (!detail.ooit_gedeeld) laadAccounts();
    setWijzigOpen(true);
  }

  async function bewaarWijzig() {
    const body = { titel: wijzigForm.titel.trim(), revisierondes: Number(wijzigForm.revisierondes) || 0 };
    if (!detail.ooit_gedeeld && (wijzigForm.portal_account_id || '') !== (detail.portal_account_id || '')) {
      const account = (accounts || []).find((a) => a.id === wijzigForm.portal_account_id) || null;
      if (!(await bevestigKoppeling(detail.klant, account))) return;
      body.portal_account_id = wijzigForm.portal_account_id || '';
    }
    await metSlot(async () => {
      try { toon(await wijzigProject(detail.id, body)); setWijzigOpen(false); }
      catch (e) { meldFout(e, 'Project opslaan'); }
    });
  }

  async function archiveer() {
    setMeerAnker(null);
    const id = detail.id;
    const titel = detail.titel;
    try {
      await wijzigProject(id, { gearchiveerd: true });
      setProjecten((lijst) => (lijst || []).filter((x) => x.id !== id));
      navigate('/ai-voortgang');
      biedOngedaanAan(`"${titel}" gearchiveerd — de klant ziet het niet meer`, async () => {
        const p = await wijzigProject(id, { gearchiveerd: false });
        await laadLijst();
        navigate(`/ai-voortgang/${p.id}`);
      });
    } catch (e) { meldFout(e, 'Archiveren'); }
  }

  const d = detail;
  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader dense icon="task_alt" title="AI Voortgang"
        subtitle="Per fase akkoord van de klant — via het klantportaal"
        rightActions={canEdit && (
          <Button variant="contained" disableElevation size="small" startIcon={<AddIcon />} onClick={() => setNieuwOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
            Nieuw project
          </Button>
        )} />

      <Box sx={{ display: 'flex', border: `1px solid ${LIJN}`, borderRadius: 3, bgcolor: '#fff', overflow: 'hidden', minHeight: 'calc(100vh - 210px)' }}>
        <Box sx={{ width: 264, flexShrink: 0, bgcolor: KOLOM_BG, borderRight: `1px solid ${LIJN}`, p: 1.25,
          display: { xs: 'none', md: 'block' }, alignSelf: 'stretch', overflowY: 'auto' }}>
          <Typography sx={{ px: 1.25, pt: 0.5, pb: 1, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: STIL }}>
            Projecten
          </Typography>
          {projecten === null ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={16} /></Box>
            : projecten.length === 0 ? <Typography sx={{ fontSize: 12.5, color: STIL, px: 1.25 }}>Nog geen projecten.</Typography>
              : projecten.map((x) => (
                <ProjectRij key={x.id} p={x} actief={x.id === projectId} onClick={() => navigate(`/ai-voortgang/${x.id}`)} />
              ))}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, px: { xs: 2, md: 5 }, py: { xs: 2.5, md: 4 } }}>
          {!projectId && (
            <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
              {(projecten || []).map((x) => <ProjectRij key={x.id} p={x} actief={false} onClick={() => navigate(`/ai-voortgang/${x.id}`)} />)}
            </Box>
          )}
          {!projectId && projecten?.length ? (
            <Inbox onOpen={(pid, itemId) => navigate(`/ai-voortgang/${pid}${itemId ? `#item-${itemId}` : ''}`)} />
          ) : !projectId ? (
            <Box sx={{ maxWidth: 560, mx: 'auto', mt: { xs: 3, md: 10 }, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: INKT }}>
                {projecten?.length ? 'Kies een project' : 'Begin met een project'}
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 14, color: STIL }}>
                Koppel een Toolframe-project aan een klantportaal-account, zet per fase een versie klaar (Frame.io-
                of bestandslink) en vraag akkoord. De klant beslist in het portaal; de volgende fase gaat pas open na akkoord.
              </Typography>
            </Box>
          ) : laden || !d ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              {laden ? <CircularProgress size={24} /> : <Typography sx={{ color: STIL }}>Project niet gevonden.</Typography>}
            </Box>
          ) : (
            <Box sx={{ maxWidth: 980, mx: 'auto' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2.5 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: INKT }}>{d.titel}</Typography>
                  <Typography sx={{ fontSize: 13.5, color: STIL }}>
                    {[d.project_number && `Project ${d.project_number}`, d.klant].filter(Boolean).join(' · ')}
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: d.portal_account_name ? STIL : WAARSCHUWING, mt: 0.25 }}>
                    {d.portal_account_name ? `Klantportaal: ${d.portal_account_name}` : 'Nog geen klantportaal-account gekoppeld'}
                    {d.status === 'archived' ? ' · gearchiveerd' : ''}
                  </Typography>
                </Box>
                {canEdit && (
                <IconButton size="small" aria-label="Meer" onClick={(e) => setMeerAnker(e.currentTarget)} sx={{ color: STIL }}>
                  <MoreHorizIcon fontSize="small" />
                </IconButton>
                )}
                <Menu anchorEl={meerAnker} open={!!meerAnker} onClose={() => setMeerAnker(null)}>
                  <MenuItem onClick={openWijzig} sx={{ fontSize: 13.5 }}>Gegevens wijzigen</MenuItem>
                  {d.portal_account_id && <MenuItem onClick={openBeoordelaars} sx={{ fontSize: 13.5 }}>Beoordelaars bij de klant…</MenuItem>}
                  {d.status === 'active' && <MenuItem onClick={archiveer} sx={{ fontSize: 13.5 }}>Archiveren</MenuItem>}
                </Menu>
              </Box>

              <Verzoeken verzoeken={d.verzoeken} canEdit={canEdit} onProject={toon} />
              {d.fases.map((f) => (
                <FaseKaart key={f.id} fase={f} bezig={bezig} canEdit={canEdit} onVraagAkkoord={akkoordVragen} onTrekTerug={terugtrekken}
                  onVersie={versieToevoegen} onVerwijderVersie={versieVerwijderen} onOverslaan={overslaan} onHeropen={heropen} optieActies={optieActies} />
              ))}

              <Typography sx={{ mt: 1, fontSize: 12, color: d.keten?.intact === false ? '#b91c1c' : STIL }}>
                {d.keten?.intact === false
                  ? `Audit: de besluitketen klopt niet meer bij besluit ${d.keten.breuk_bij} (${d.keten.reden}) — meld dit bij Joris.`
                  : `Audit: ${d.keten?.aantal || 0} klantbesluit(en), keten intact.`}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <NieuwProjectDialoog open={nieuwOpen} onClose={() => setNieuwOpen(false)} opslaan={nieuwProject}
        onAangemaakt={(p) => {
          setNieuwOpen(false);
          setProjecten((lijst) => [p, ...(lijst || [])]);
          navigate(`/ai-voortgang/${p.id}`);
        }} />

      <Dialog open={!!beoordelaars} onClose={() => setBeoordelaars(null)} fullWidth maxWidth="sm">
        <DialogTitle>Beoordelaars bij {d?.portal_account_name || 'de klant'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: STIL, mb: 1.5 }}>
            Iedereen met een goedgekeurd account bij de klant ziet het project. Kies per persoon wat hij of zij mag:
            akkoord geven, juridisch akkoord (alleen bij onderdelen die dat vragen) of alleen meekijken.
          </Typography>
          {beoordelaars?.laden && <CircularProgress size={18} />}
          {beoordelaars && !beoordelaars.laden && beoordelaars.lijst.length === 0 && (
            <Typography sx={{ fontSize: 13.5, color: STIL }}>Nog geen goedgekeurde gebruikers bij dit account.</Typography>
          )}
          {(beoordelaars?.lijst || []).map((b) => (
            <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderTop: `1px solid ${LIJN}` }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: INKT }}>{b.naam}</Typography>
                <Typography noWrap sx={{ fontSize: 12, color: STIL }}>{b.email}</Typography>
              </Box>
              <TextField select size="small" value={b.rol} onChange={(e) => rolWijzigen(b, e.target.value)} sx={{ minWidth: 190 }}
                inputProps={{ 'aria-label': `Rol van ${b.naam}` }}>
                {Object.entries(ROLLEN).map(([k, r]) => <MenuItem key={k} value={k}>{r.label}</MenuItem>)}
              </TextField>
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setBeoordelaars(null)} sx={{ textTransform: 'none' }}>Sluiten</Button></DialogActions>
      </Dialog>

      <Dialog open={!!hernoem} onClose={() => setHernoem(null)} fullWidth maxWidth="xs">
        <DialogTitle>Optie hernoemen</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField fullWidth size="small" autoFocus label="Naam van de optie" value={hernoem?.label || ''} inputProps={{ maxLength: 200 }}
            onChange={(e) => setHernoem((h) => ({ ...h, label: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') bewaarHernoem(); }}
            helperText="De klant ziet deze naam, bv. 'Warm licht' of 'Variant met hond'" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHernoem(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" disableElevation onClick={bewaarHernoem} disabled={!hernoem?.label.trim() || bezig}
            sx={{ textTransform: 'none', bgcolor: ACCENT }}>Opslaan</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={wijzigOpen} onClose={() => { if (!bezig) setWijzigOpen(false); }} fullWidth maxWidth="xs">
        <DialogTitle>Project wijzigen</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField size="small" label="Titel" value={wijzigForm.titel} inputProps={{ maxLength: 200 }}
            onChange={(e) => setWijzigForm((f) => ({ ...f, titel: e.target.value }))} />
          <TextField size="small" type="number" label="Inbegrepen correctierondes per item" value={wijzigForm.revisierondes}
            inputProps={{ min: 0, max: 20 }} onChange={(e) => setWijzigForm((f) => ({ ...f, revisierondes: e.target.value }))} />
          {d?.ooit_gedeeld ? (
            <Typography sx={{ fontSize: 12.5, color: STIL }}>
              Klantportaal-account: {d.portal_account_name || '—'} (ligt vast: er is al iets met de klant gedeeld).
            </Typography>
          ) : (
            <AccountKeuze accounts={accounts} fout={accountFout} waarde={wijzigForm.portal_account_id} klant={d?.klant}
              onChange={(v) => setWijzigForm((f) => ({ ...f, portal_account_id: v }))} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWijzigOpen(false)} disabled={bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button variant="contained" disableElevation onClick={bewaarWijzig} disabled={bezig || !wijzigForm.titel.trim()}
            sx={{ textTransform: 'none', bgcolor: ACCENT }}>Opslaan</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
